import { EventDispatcher, Quaternion, Vector3, } from '@seddi/three';
import { OrbitControls } from '@/viewer/orbitControls';
import { PickControls } from './pickControls';
import { TransformControls } from './transformControls';
import { ViewerFrameControls } from './viewerFrameControls';
import { Raycaster } from './viewerRaycaster';
/**
 * Return true when the selected logo updates the region.
 * @param prevLogos - Prev logos state.
 * @param nextLogos - Next logos state.
 * @param selection - Current logo selection ID.
 */
const isRegionUpdateOnSelection = (prevLogos, nextLogos, selection) => {
    if (!selection)
        return false;
    const prevLogo = prevLogos.find(l => l.id === selection);
    const nextLogo = nextLogos.find(l => l.id === selection);
    if (prevLogo && nextLogo) {
        return prevLogo.region !== nextLogo.region;
    }
    return false;
};
export class AppViewerTool extends EventDispatcher {
    constructor({ camera, canvas, dispatch, enableRaycast = false, enableTransform = false, idleAnimationMode = 'static', }) {
        super();
        this.logos = [];
        this.isFrameDone = false;
        this.zoomDirection = new Vector3();
        this.targetPrev = new Vector3();
        this.hasUserInteracted = false;
        this.cameraPositionPrev = new Vector3();
        this.cameraRotationPrev = new Quaternion();
        this.camera = camera;
        this.canvas = canvas;
        this.enableRaycast = enableRaycast;
        this.enableTransform = enableTransform;
        this.idleAnimationMode = idleAnimationMode;
        this.raycaster = enableRaycast ? new Raycaster() : null;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const scope = this;
        this.frameControls = new ViewerFrameControls(camera, canvas);
        this.frameControls.enabled = false;
        this.frameControls.addEventListener('end', onFrameEnd);
        this.orbitControls = new OrbitControls(camera, canvas);
        this.orbitControls.dampingFactor = 0.1;
        this.orbitControls.enableDamping = true;
        this.orbitControls.autoRotatePeriodSeconds = 30;
        this.orbitControls.autoRotate = false;
        this.orbitControls.addEventListener('start', () => {
            scope.markUserActive();
        });
        if (enableRaycast) {
            this.pickControls = new PickControls(camera, canvas, this.raycaster, dispatch);
            this.transformControls = new TransformControls(canvas, this.raycaster, dispatch);
            this.setupPointerHandlers();
        }
        else {
            this.pickControls = null;
            this.transformControls = null;
        }
        this.lookAt({ x: 0.0, y: 1.2, z: 3.0 }, { x: 0.0, y: 1.2, z: 0.0 });
        this.applyIdlePolicyAfterProgrammaticCameraChange();
        this.dispose = () => {
            scope.frameControls.removeEventListener('end', onFrameEnd);
            scope.clearIdleResumeTimer();
            if (enableRaycast) {
                canvas.removeEventListener('pointerdown', this.onPointerDownBound, { capture: true });
                canvas.removeEventListener('pointermove', this.onPointerMoveBound);
                canvas.removeEventListener('pointerup', this.onPointerUpBound, { capture: true });
                this.pickControls.dispose();
                this.transformControls.dispose();
                this.raycaster.dispose();
            }
            this.orbitControls.dispose();
        };
        function onFrameEnd() {
            scope.orbitControls.target.copy(scope.frameControls.frame.center);
            scope.isFrameDone = true;
        }
    }
    setupPointerHandlers() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const scope = this;
        const canvas = this.canvas;
        this.onPointerDownBound = function (event) {
            scope.markUserActive();
            if (scope.enableRaycast) {
                scope.pickControls.onPointerDown(event);
                if (scope.enableTransform) {
                    scope.transformControls.onPointerDown(event);
                    if (scope.transformControls.isDown) {
                        scope.orbitControls.enabled = false;
                    }
                }
            }
        };
        this.onPointerMoveBound = function (event) {
            if (scope.enableRaycast) {
                scope.pickControls.onPointerMove(event);
                scope.transformControls.onPointerMove(event);
            }
        };
        this.onPointerUpBound = function () {
            scope.orbitControls.enabled = true;
        };
        canvas.addEventListener('pointerdown', this.onPointerDownBound, { capture: true });
        canvas.addEventListener('pointermove', this.onPointerMoveBound, { capture: true });
        canvas.addEventListener('pointerup', this.onPointerUpBound, { capture: true });
    }
    lookAt(origin, target) {
        this.camera.position.copy(origin);
        this.camera.lookAt(target.x, target.y, target.z);
        this.orbitControls.target.copy(target);
        this.applyIdlePolicyAfterProgrammaticCameraChange();
    }
    setMaxDistance(distance) {
        this.orbitControls.maxDistance = distance;
    }
    zoomIn() {
        this.adjustZoom('in');
    }
    zoomOut() {
        this.adjustZoom('out');
    }
    adjustZoom(direction) {
        const target = this.orbitControls.target;
        this.zoomDirection.copy(this.camera.position).sub(target);
        const currentDistance = this.zoomDirection.length();
        if (currentDistance === 0) {
            return;
        }
        const zoomScale = Math.pow(0.95, this.orbitControls.zoomSpeed);
        const scaleFactor = direction === 'in' ? zoomScale : 1 / zoomScale;
        let newDistance = currentDistance * scaleFactor;
        newDistance = Math.max(this.orbitControls.minDistance, Math.min(this.orbitControls.maxDistance, newDistance));
        this.zoomDirection.setLength(newDistance);
        this.camera.position.copy(target).add(this.zoomDirection);
    }
    onAnimationTick() {
        this.frameControls.update();
        if (this.frameControls.enabled && this.frameControls.target) {
            this.orbitControls.target.copy(this.frameControls.target);
        }
        this.orbitControls.update();
        if (this.enableRaycast) {
            this.pickControls.update();
        }
        if (this.isFrameDone) {
            this.isFrameDone = false;
            this.frameControls.enabled = false;
            this.orbitControls.enabled = true;
            if (this.enableRaycast) {
                this.pickControls.enabled = true;
                if (this.enableTransform) {
                    this.transformControls.enabled = true;
                }
            }
            this.applyIdlePolicyAfterProgrammaticCameraChange();
        }
        const isCameraPositionDirty = !approxEqualsVector3(this.cameraPositionPrev, this.camera.position, 1e-4);
        const isCameraRotationDirty = !approxEqualsQuaternion(this.cameraRotationPrev, this.camera.quaternion, 1e-4);
        const isCameraTransformDirty = isCameraPositionDirty || isCameraRotationDirty;
        if (isCameraTransformDirty) {
            this._emitCameraChange();
        }
        this.cameraPositionPrev.copy(this.camera.position);
        this.cameraRotationPrev.copy(this.camera.quaternion);
        return isCameraTransformDirty;
    }
    onUpdate() { }
    clearIdleResumeTimer() {
        if (this.idleResumeTimeoutId !== undefined) {
            clearTimeout(this.idleResumeTimeoutId);
            this.idleResumeTimeoutId = undefined;
        }
    }
    /**
     * Called after camera/target are updated programmatically (scene load framing, reset view, etc).
     * This should not count as user activity, but it may affect whether we show idle rotation.
     */
    applyIdlePolicyAfterProgrammaticCameraChange() {
        if (this.idleAnimationMode !== 'rotate')
            return;
        // Always stop any existing rotation and timers on camera jumps.
        this.orbitControls.autoRotate = false;
        this.clearIdleResumeTimer();
        // If the user hasn't interacted yet, start rotating immediately (teaches "this is 3D").
        if (!this.hasUserInteracted) {
            this.orbitControls.autoRotate = true;
            return;
        }
        // If the user *has* interacted, resume only after inactivity.
        this.idleResumeTimeoutId = window.setTimeout(() => {
            if (this.frameControls.enabled)
                return;
            if (!this.orbitControls.enabled)
                return;
            if (this.idleAnimationMode !== 'rotate')
                return;
            this.orbitControls.autoRotate = true;
        }, 30000);
    }
    markUserActive() {
        this.hasUserInteracted = true;
        if (this.idleAnimationMode === 'rotate') {
            this.orbitControls.autoRotate = false;
            this.clearIdleResumeTimer();
            this.idleResumeTimeoutId = window.setTimeout(() => {
                // Don't resume while framing / disabled.
                if (this.frameControls.enabled)
                    return;
                if (!this.orbitControls.enabled)
                    return;
                if (this.idleAnimationMode !== 'rotate')
                    return;
                this.orbitControls.autoRotate = true;
            }, 30000);
        }
    }
    updateGarmentGeometry(geometry) {
        this.frameControls.updateGarmentGeometry(geometry);
        if (this.enableRaycast && geometry) {
            this.raycaster.buildGarmentGeometry(geometry);
        }
    }
    updateModel(model) {
        if (model) {
            model.updateWorldMatrix(false, true);
            this.frameControls.updateModel(model);
        }
        if (this.enableRaycast && model) {
            this.raycaster.buildModel(model);
        }
    }
    updateLogos(logos) {
        if (this.enableRaycast) {
            this.raycaster.updateLogos(logos);
            this.transformControls.logos = logos;
        }
        // Frame selection when logo region changes.
        const isFrameRequired = isRegionUpdateOnSelection(this.logos, logos, this.selection);
        if (isFrameRequired) {
            this.frameControls.updateSelection(logos, this.selection);
            this.frameControls.enabled = true;
            this.orbitControls.enabled = false;
            if (this.enableRaycast) {
                this.pickControls.enabled = false;
                if (this.enableTransform) {
                    this.transformControls.enabled = false;
                }
            }
        }
        this.logos = logos;
    }
    updateRegions(regions) {
        this.frameControls.updateRegions(regions);
        if (this.enableRaycast) {
            this.raycaster.updateRegions(regions);
            this.pickControls.enabled = true;
            if (this.enableTransform) {
                this.transformControls.enabled = true;
            }
        }
    }
    updateSelection(selection) {
        this.selection = selection !== null && selection !== void 0 ? selection : undefined;
        if (this.enableRaycast) {
            this.pickControls.selection = this.selection;
            this.transformControls.selection = this.selection;
        }
        this.frameControls.updateSelection(this.logos, this.selection);
        // Only frame on selection, not on deselection.
        if (this.selection) {
            this.frameControls.enabled = true;
            this.orbitControls.enabled = false;
            if (this.enableRaycast) {
                this.pickControls.enabled = false;
                if (this.enableTransform) {
                    this.transformControls.enabled = false;
                }
            }
        }
    }
    updateHover(hover) {
        if (this.enableRaycast) {
            this.pickControls.hover = hover !== null && hover !== void 0 ? hover : undefined;
        }
    }
    _emitCameraChange() {
        const { camera } = this;
        const { position, quaternion } = camera;
        this.dispatchEvent({ position, quaternion, type: 'cameraChange' });
    }
}
const approxEqualsQuaternion = (a, b, epsilon = Number.EPSILON) => {
    return (Math.abs(a.w - b.w) < epsilon &&
        Math.abs(a.x - b.x) < epsilon &&
        Math.abs(a.y - b.y) < epsilon &&
        Math.abs(a.z - b.z) < epsilon);
};
const approxEqualsVector3 = (a, b, epsilon = Number.EPSILON) => {
    return (Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.z - b.z) < epsilon);
};
